import { useCallback, useEffect, useRef, useState } from "react";
import { ImageDown, Loader2, RefreshCw, ScanSearch, AlertTriangle, Camera, Check, Images, FileWarning } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Re-hosts KB images that still point at an outside host. Runs in whichever
 *  environment you're looking at, so production can be fixed in place rather
 *  than mirrored from preview (a mirror would overwrite articles fixed live). */
export default function KbImageRescueCard() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState("");
  const [guidde, setGuidde] = useState(null);
  const [showGuidde, setShowGuidde] = useState(false);
  const [repair, setRepair] = useState(null);
  const [lost, setLost] = useState(null);
  const timer = useRef(null);
  const isSuper = user?.role === "super_admin";

  const runScan = useCallback(async (quiet = false) => {
    try {
      const { data } = await api.get("/kb/image-rescue/scan");
      setScan(data);
      return data;
    } catch (e) {
      if (!quiet) toast.error(e.response?.data?.detail || "Scan failed");
      return null;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kb/image-rescue");
      setState((prev) => {
        // The run just finished — re-count what's left so the card answers
        // "how many still to do?" without anyone pressing Scan.
        if (prev?.running && !data.running) {
          runScan(true).then((s) => {
            if (s) {
              toast.success(s.external_images
                ? `Rescue finished — ${s.external_images} images still on outside hosts`
                : "Rescue finished — every image is now on our own storage");
            }
          });
        }
        return data;
      });
      return data;
    } catch {
      return null;
    }
  }, [runScan]);

  useEffect(() => { load(); runScan(true); }, [load, runScan]);

  const loadGuidde = useCallback(async () => {
    try {
      const { data } = await api.get("/kb/image-rescue/guidde");
      setGuidde(data);
      return data;
    } catch { return null; }
  }, []);

  useEffect(() => { loadGuidde(); }, [loadGuidde]);

  // Poll while the Guidde copy is in flight.
  useEffect(() => {
    if (!guidde?.running) return undefined;
    const t = setInterval(async () => {
      const data = await loadGuidde();
      if (data && !data.running) {
        clearInterval(t);
        toast.success(data.missing
          ? `Copied — ${data.stored} of ${data.references} held, ${data.missing} could not be fetched`
          : `All ${data.references} Guidde screenshots are in the media library`);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [guidde?.running, loadGuidde]);

  const doGuidde = async () => {
    setBusy("guidde");
    try {
      await api.post("/kb/image-rescue/guidde");
      toast.success("Copying the Guidde screenshots — no article will be changed");
      await loadGuidde();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the copy");
    } finally { setBusy(""); }
  };

  // Poll only while a run is in flight.
  useEffect(() => {
    if (!state?.running) {
      if (timer.current) { clearInterval(timer.current); timer.current = null; }
      return undefined;
    }
    timer.current = setInterval(load, 4000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [state?.running, load]);

  const doScan = async () => {
    setBusy("scan");
    const data = await runScan();
    if (data) {
      toast.success(data.external_images
        ? `${data.external_images} images on outside hosts across ${data.articles_affected} articles`
        : "Every image is on our own storage");
    }
    setBusy("");
  };

  const doRun = async () => {
    const n = scan?.external_images;
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Re-host ${n ? `${n} ` : ""}externally linked images?\n\n`
      + "Only the <img src> values change — your text, tables and formatting are untouched, "
      + "and every article is snapshotted first so it can be rolled back.\n\n"
      + "This runs against the database you are looking at right now.")) return;
    setBusy("run");
    try {
      await api.post("/kb/image-rescue/run");
      toast.success("Rescue started — progress updates here as it works");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the rescue");
    } finally { setBusy(""); }
  };

  const doPlace = async () => {
    const total = guidde?.stored || 0;
    const names = (guidde?.articles || []).map((a) => `· ${a.name} (${a.stored})`).join("\n");
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Point ${total} images at our own copies, in these articles?\n\n${names}\n\n`
      + "Only the <img src> values change — wording, tables and formatting are untouched, "
      + "and each article is snapshotted first so History rolls it straight back.")) return;
    setBusy("place");
    try {
      const { data } = await api.post("/kb/image-rescue/guidde/place");
      toast.success(`Placed ${data.images_placed} images across ${data.articles_changed} articles`);
      await Promise.all([loadGuidde(), runScan(true)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place the screenshots");
    } finally { setBusy(""); }
  };

  const loadRepair = useCallback(async () => {
    try {
      const { data } = await api.get("/kb/asset-repair");
      setRepair(data);
      return data;
    } catch { return null; }
  }, []);

  useEffect(() => { loadRepair(); }, [loadRepair]);

  // Poll while a check or refill is in flight.
  useEffect(() => {
    if (!repair?.scanning && !repair?.repairing) return undefined;
    const t = setInterval(async () => {
      const data = await loadRepair();
      if (data && !data.scanning && !data.repairing) {
        clearInterval(t);
        toast.success(data.missing
          ? `${data.missing} image files are missing — ${data.recoverable} can be refilled`
          : "Every image file is present in our storage");
      }
    }, 3000);
    return () => clearInterval(t);
  }, [repair?.scanning, repair?.repairing, loadRepair]);

  const doRepairScan = async () => {
    setBusy("repair-scan");
    try {
      await api.post("/kb/asset-repair/scan");
      toast.success("Checking every image file — this takes a few minutes");
      await loadRepair();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the check");
    } finally { setBusy(""); }
  };

  const doRepairRun = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Re-download ${repair?.recoverable || 0} missing image files from where they came from?\n\n`
      + "Articles aren't touched — this only puts the missing files back in our storage.")) return;
    setBusy("repair-run");
    try {
      await api.post("/kb/asset-repair/run");
      toast.success("Refilling — progress updates here");
      await loadRepair();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the refill");
    } finally { setBusy(""); }
  };

  const showLost = async () => {
    if (lost) { setLost(null); return; }
    try {
      const { data } = await api.get("/kb/asset-repair/lost", { params: { limit: 100 } });
      setLost(data);
    } catch { toast.error("Could not load that list"); }
  };

  const pct = state?.total ? Math.round((100 * (state.progress || 0)) / state.total) : 0;

  return (
    <Card className="p-4" data-testid="kb-image-rescue-card">
      <div className="mb-1 flex items-center gap-2">
        <ImageDown className="h-4 w-4 text-slate-500 dark:text-slate-400" />
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Image hosting</div>
      </div>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
        Articles imported from Helpjuice still link to images on Helpjuice&apos;s servers. This
        downloads each one into our own storage and re-points the article at our copy, so the
        screenshots survive that account going away. Safe to re-run — anything already ours is
        skipped.
      </p>

      {scan && (
        <div className="mb-3 rounded border border-slate-200 dark:border-slate-800 p-2 text-xs" data-testid="kb-image-rescue-scan">
          <div className={scan.external_images
            ? "text-slate-700 dark:text-slate-200"
            : "font-semibold text-emerald-700 dark:text-emerald-400"}>
            {scan.external_images ? (
              <>
                <b>{scan.external_images}</b> images still to do, across <b>{scan.articles_affected}</b> articles
                {" · "}<b>{scan.already_self_hosted}</b> already ours
              </>
            ) : (
              <>All {scan.already_self_hosted} images are on our own storage — nothing left to do</>
            )}
          </div>
          {(scan.by_host || []).slice(0, 4).map((h) => (
            <div key={h.host} className="mt-0.5 font-mono text-[11px] text-slate-500 dark:text-slate-400">
              {h.images} · {h.host} ({h.articles} articles)
            </div>
          ))}
        </div>
      )}

      {state?.running && (
        <div className="mb-3" data-testid="kb-image-rescue-progress">
          <div className="mb-1 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <Loader2 className="h-3 w-3 animate-spin" />
            {state.progress || 0} / {state.total || "?"} articles · {state.images_moved || 0} moved
            {" · "}{state.images_reused || 0} reused
          </div>
          <div className="h-1.5 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {!state?.running && state?.finished && (
        <div className="mb-3 text-xs text-slate-600 dark:text-slate-300" data-testid="kb-image-rescue-last">
          Last run finished {String(state.finished).slice(0, 16).replace("T", " ")} ·{" "}
          {state.articles_changed || 0} articles updated · {state.images_moved || 0} images moved,{" "}
          {state.images_reused || 0} reused
          {state.failed ? (
            <span className="ml-1 inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3" />
              {state.failed} could not be fetched
            </span>
          ) : null}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={doScan} disabled={!!busy}
                data-testid="kb-image-rescue-scan-btn">
          {busy === "scan" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            : <ScanSearch className="mr-1 h-3.5 w-3.5" />}
          Scan
        </Button>
        <Button size="sm" onClick={doRun} disabled={!!busy || !isSuper || state?.running}
                data-testid="kb-image-rescue-run-btn">
          {busy === "run" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
          Re-host images now
        </Button>
        {!isSuper && (
          <span className="text-[11px] text-slate-500 dark:text-slate-400">Super admins can run it</span>
        )}
      </div>

      {(!!guidde?.references || !!guidde?.placed_images) && (
        <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-3"
             data-testid="kb-guidde-section">
          <div className="mb-1 flex items-center gap-2">
            <Camera className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
              Guidde walkthrough screenshots
            </div>
          </div>
          <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
            The Salesforce walkthroughs load their screenshots from Guidde, whose links expire.
            <b> Copy</b> brings each one into our media library (search
            {" "}<span className="font-mono">guidde</span>) without touching a single article;
            <b> Place</b> then points the articles at our copies for you.
          </p>

          <div className="mb-2 text-xs" data-testid="kb-guidde-summary">
            {!guidde.references ? (
              <span className="inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-400">
                <Check className="h-3.5 w-3.5" />
                Placed — {guidde.placed_images} images across {guidde.placed_articles} articles,
                nothing left on Guidde
              </span>
            ) : guidde.missing ? (
              <span className="text-slate-700 dark:text-slate-200">
                <b>{guidde.stored}</b> of <b>{guidde.references}</b> held ·{" "}
                <b>{guidde.missing}</b> still to copy, across {guidde.articles.length} articles
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-400">
                <Check className="h-3.5 w-3.5" />
                All {guidde.references} screenshots are in the media library — ready to place
              </span>
            )}
          </div>

          {guidde.running && (
            <div className="mb-2 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
                 data-testid="kb-guidde-progress">
              <Loader2 className="h-3 w-3 animate-spin" />
              {guidde.progress || 0} / {guidde.total || "?"} · {guidde.stored || 0} copied,{" "}
              {guidde.reused || 0} already held
            </div>
          )}

          {showGuidde && (
            <div className="mb-2 space-y-2" data-testid="kb-guidde-detail">
              {guidde.articles.map((a) => (
                <div key={a.slug} className="rounded border border-slate-200 dark:border-slate-800 p-2">
                  <div className="text-xs font-medium text-slate-700 dark:text-slate-200">
                    {a.name} <span className="font-normal text-slate-400">· {a.stored}/{a.images} held</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {a.assets.map((row) => (
                      <a key={row.asset_id} href={row.url} target="_blank" rel="noreferrer"
                         title={row.have ? row.url : "Not copied yet"}
                         className={`font-mono text-[10px] rounded px-1.5 py-0.5 ${row.have
                           ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                           : "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300"}`}>
                        {String(row.n).padStart(2, "0")}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {!!guidde.articles.length && (
              <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => setShowGuidde((v) => !v)} data-testid="kb-guidde-toggle">
                {showGuidde ? "Hide the list" : "Show each screenshot"}
              </Button>
            )}
            <Button size="sm" className="h-7 text-xs" onClick={doGuidde}
                    disabled={!!busy || !isSuper || guidde.running}
                    data-testid="kb-guidde-run-btn">
              {busy === "guidde" || guidde.running
                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Camera className="mr-1 h-3.5 w-3.5" />}
              {guidde.missing ? "Copy them into our storage" : "Re-check and re-copy"}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={doPlace}
                    disabled={!!busy || !isSuper || guidde.running || !guidde.stored}
                    data-testid="kb-guidde-place-btn">
              {busy === "place" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Images className="mr-1 h-3.5 w-3.5" />}
              Place them in the articles
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
            Placing swaps each <span className="font-mono">img src</span> for our copy, in the
            order above — wording and layout are untouched, and every article is snapshotted
            first, so History undoes it.
          </p>
        </div>
      )}
      {!!repair?.assets && (
        <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-3"
             data-testid="kb-asset-repair-section">
          <div className="mb-1 flex items-center gap-2">
            <FileWarning className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
              Missing image files
            </div>
          </div>
          <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
            An image can be listed here with its name and size while the file itself has gone
            from storage — the article then shows a broken picture and the media library shows a
            broken thumbnail. This checks all {repair.assets.toLocaleString()} of them and
            re-downloads anything that still knows where it came from.
          </p>

          <div className="mb-2 text-xs" data-testid="kb-asset-repair-summary">
            {repair.storage_checked === undefined && !repair.scan_finished ? (
              <span className="text-slate-500 dark:text-slate-400">Not checked yet</span>
            ) : repair.missing ? (
              <span className="text-slate-700 dark:text-slate-200">
                <b className="text-amber-700 dark:text-amber-400">{repair.missing}</b> files
                missing · <b>{repair.recoverable}</b> can be re-downloaded ·{" "}
                <b>{repair.unrecoverable}</b> need a manual re-upload
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-400">
                <Check className="h-3.5 w-3.5" /> Every image file is present
              </span>
            )}
          </div>

          {(repair.scanning || repair.repairing) && (
            <div className="mb-2 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
                 data-testid="kb-asset-repair-progress">
              <Loader2 className="h-3 w-3 animate-spin" />
              {repair.scanning
                ? <>Checking {repair.scan_progress || 0} / {repair.scan_total || "?"} · {repair.missing || 0} missing so far</>
                : <>Refilling {repair.repair_progress || 0} / {repair.repair_total || "?"} · {repair.refilled || 0} back, {repair.gave_up || 0} failed</>}
            </div>
          )}

          {!repair.scanning && repair.scan_finished && (
            <div className="mb-2 text-[11px] text-slate-500 dark:text-slate-400">
              Last checked {String(repair.scan_finished).slice(0, 16).replace("T", " ")}
              {repair.repair_finished ? <> · last refill {String(repair.repair_finished).slice(0, 16).replace("T", " ")} put {repair.refilled || 0} back</> : null}
            </div>
          )}

          <div className="mb-2 text-[11px] text-slate-500 dark:text-slate-400"
               data-testid="kb-storage-watch-line">
            {repair.watch?.last_checked ? (
              <>Watched daily · last sample {String(repair.watch.last_checked).slice(0, 16).replace("T", " ")}
                {" "}({repair.watch.last_sample || 0} files, {repair.watch.last_missing || 0} missing)
                {repair.watch.last_alert_at
                  ? <> · last alert {String(repair.watch.last_alert_at).slice(0, 10)} to {repair.watch.last_alert_recipients || 0} super admins</>
                  : " · no alert needed yet"}</>
            ) : (
              <>A daily watch samples 300 files and emails super admins the moment pictures stop loading.</>
            )}
          </div>

          {!!lost?.assets?.length && (
            <div className="mb-2 max-h-56 space-y-1 overflow-auto rounded border border-slate-200 dark:border-slate-800 p-2"
                 data-testid="kb-asset-repair-lost">
              {lost.assets.map((a) => (
                <div key={a.asset_id} className="text-[11px]">
                  <span className="font-medium text-slate-700 dark:text-slate-200">{a.filename}</span>
                  <span className="text-slate-400"> · {a.uploaded_by || "unknown"}
                    {a.refill_error ? ` · ${a.refill_error}` : " · never had a source"}</span>
                  {a.articles.map((art) => (
                    <a key={art.slug} href={`/kb/a/${art.slug}`} target="_blank" rel="noreferrer"
                       className="ml-1 text-primary hover:underline">{art.name}</a>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={doRepairScan}
                    disabled={!!busy || repair.scanning || repair.repairing}
                    data-testid="kb-asset-repair-scan-btn">
              {busy === "repair-scan" || repair.scanning
                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <ScanSearch className="mr-1 h-3.5 w-3.5" />}
              Check every file
            </Button>
            <Button size="sm" className="h-7 text-xs" onClick={doRepairRun}
                    disabled={!!busy || !isSuper || repair.scanning || repair.repairing
                              || !repair.recoverable}
                    data-testid="kb-asset-repair-run-btn">
              {busy === "repair-run" || repair.repairing
                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
              Put {repair.recoverable || 0} files back
            </Button>
            {!!repair.unrecoverable && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={showLost}
                      data-testid="kb-asset-repair-lost-btn">
                {lost ? "Hide" : `Show the ${repair.unrecoverable} to re-upload`}
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
